/** The config diff between a sandbox and its production operator (S5a; SBX D3 "Config diff"), for either
 *  side of the pair: every differing leaf, credentials compared on their values but shown masked; the
 *  sandbox's own state (`config.sandbox`) is not part of it. Read-only — a later "promote to production"
 *  builds on it as a change set (metaplan M13). */
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import {
  sandboxApi,
  sandboxErrorMessage,
  sandboxKeys,
  type SandboxConfigDifference,
} from '@/api/sandbox';

const OP_LABEL: Record<SandboxConfigDifference['op'], string> = {
  added: 'Only in the sandbox',
  removed: 'Only in production',
  changed: 'Different',
};

function show(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

export function ConfigDiffSection({ topId }: { topId: number }) {
  const query = useQuery({
    queryKey: sandboxKeys.configDiff(topId),
    queryFn: () => sandboxApi.configDiff(topId),
  });
  if (query.isLoading) return <CircularProgress size={20} />;
  if (query.error)
    return (
      <Alert severity="error">
        {sandboxErrorMessage(query.error, 'Couldn’t load the config diff.')}
      </Alert>
    );
  const diff = query.data;
  if (!diff) return null;
  if (diff.differences.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {diff.sandbox.code} and {diff.production.code} have the same configuration (outside the
        sandbox’s own settings).
      </Typography>
    );
  }
  return (
    <Box
      sx={{ maxHeight: 480, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}
    >
      <Table size="small" stickyHeader aria-label="Config differences">
        <TableHead>
          <TableRow>
            <TableCell>Key</TableCell>
            <TableCell>Difference</TableCell>
            <TableCell>Production ({diff.production.code})</TableCell>
            <TableCell>Sandbox ({diff.sandbox.code})</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {diff.differences.map((d) => (
            <TableRow key={d.path}>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>
                {d.path}
              </TableCell>
              <TableCell>
                <Chip size="small" variant="outlined" label={OP_LABEL[d.op]} />
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 13,
                  wordBreak: 'break-all',
                  color: d.op === 'added' ? 'text.disabled' : undefined,
                }}
              >
                {show(d.production)}
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 13,
                  wordBreak: 'break-all',
                  color: d.op === 'removed' ? 'text.disabled' : undefined,
                }}
              >
                {show(d.sandbox)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
